import {
  Badge,
  Button,
  CheckBox,
  DragList,
  Flex,
  IconButton,
  InputContainer,
  Menu,
  MenuOption,
  SimpleGrid,
  Spacer,
  Text,
  TextField,
  Tooltip,
} from 'components'
import { DotsDragIcon, ExclamationCircleIcon, PlusIcon, XMarkIcon } from 'components/icons'
import { useWorkspace } from 'components/providers/WorkspaceProvider'
import { startCase } from 'lodash-es'
import { useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router'
import {
  FormField,
  PrimaryLeadProperty,
  RegistrationConsentReference,
  TemplateRegistration,
  WorkspaceProperty,
  WorkspacePropertyType,
  primaryLeadProperties,
} from 'types'
import { generateId } from 'utils'
import { useRegistrationConsents } from '../../utils/registrationConsentUtils'
import { RoutePath } from '../../utils/routing'
import { useTemplate } from './TemplateProvider'

export default function ManageTemplateRegistration() {
  const { template, updateTemplate } = useTemplate()

  if (!template) return null

  const { consents = [], fields } = template.registration || {}

  function updateRegistration(body: Partial<TemplateRegistration>) {
    updateTemplate({ registration: { ...template?.registration, ...body } })
  }

  return (
    <Flex direction="column" gap={8}>
      <Consents
        consents={consents}
        onChange={newConsents => updateRegistration({ consents: newConsents })}
      />
      <FormFields
        fields={fields}
        onChange={newFields => updateRegistration({ fields: newFields })}
      />
    </Flex>
  )
}

interface ConsentsProps {
  consents: RegistrationConsentReference[]
  onChange: (consents: RegistrationConsentReference[]) => void
}

function Consents({ consents, onChange }: ConsentsProps) {
  const navigate = useNavigate()
  const { workspace } = useWorkspace()
  const { registrationConsents } = useRegistrationConsents()

  const getConsentTitle = (consentId: string) => {
    const consent = registrationConsents.find(c => c.id === consentId)
    return consent?.title || 'Unknown Consent'
  }

  const getRegionLabel = (region: string) => {
    return region === 'default' ? 'Default' : 'Rest of World'
  }

  const addConsent = (consentId: string) => {
    if (consentId === 'create_new') {
      navigate(generatePath(RoutePath.RegistrationConsents, { workspaceId: workspace!.id }))
      return
    }

    const region = consents.length === 0 ? 'default' : 'row'
    const newConsent: RegistrationConsentReference = { region, consentId }
    onChange([...consents, newConsent])
  }

  const getAvailableConsents = (): MenuOption<string>[] => {
    const usedConsentIds = consents.map(c => c.consentId)

    const availableConsents = registrationConsents
      .filter(c => !usedConsentIds.includes(c.id))
      .map(c => ({
        label: c.title,
        value: c.id,
      }))

    return [...availableConsents, { label: '+ Create New Consent', value: 'create_new' }]
  }

  return (
    <Flex direction="column" gap={4}>
      <Text textStyle="h5">Consents</Text>

      <Flex direction="column" alignItems="flex-start" alignSelf="flex-start">
        {consents.map(({ consentId, region }) => (
          <Flex
            key={consentId}
            borderRadius="sm"
            py={1}
            px={2}
            _hover={{ backgroundColor: 'gray.300' }}
            onClick={() =>
              navigate(
                generatePath(RoutePath.RegistrationConsentInfo, {
                  workspaceId: workspace!.id,
                  id: consentId,
                })
              )
            }
            cursor="pointer"
            alignItems="center"
            gap={2}
          >
            <Text>{getConsentTitle(consentId)}</Text>
            <Badge>{getRegionLabel(region)}</Badge>
            <IconButton
              size="sm"
              icon={<XMarkIcon />}
              aria-label="Remove Consent"
              onClick={e => {
                e.stopPropagation()
                onChange(consents.filter(c => c.consentId !== consentId))
              }}
            />
          </Flex>
        ))}
      </Flex>

      {consents.length < 2 && (
        <Menu
          alignSelf="flex-start"
          leftIcon={<PlusIcon />}
          buttonLabel={`Add ${!consents.length ? 'Default' : 'Rest of World'} Consent`}
          options={getAvailableConsents()}
          onChange={value => addConsent(value as string)}
        />
      )}
    </Flex>
  )
}

interface FormFieldProps {
  fields?: FormField[]
  onChange: (fields: FormField[]) => void
}

type FieldWithId = FormField & { id: string }

function FormFields({ fields, onChange }: FormFieldProps) {
  const [draft, setDraft] = useState<FieldWithId[]>([])
  const [selectedId, setSelectedId] = useState<string>()

  const selectedField = draft.find(f => f.id === selectedId)
  const propertyNames = fields?.map(f => f.property) || []
  const duplicateProperties = propertyNames.filter((val, i) => propertyNames.includes(val, i + 1))

  useEffect(() => {
    const _fields = fields || defaultFields
    const draft = _fields.map(field => ({ ...field, id: generateId() }))
    setDraft(draft)
    setSelectedId(draft[0]?.id)
  }, [])

  function handleAdd() {
    const id = generateId()
    handleChange([...draft, { id, property: '', label: { en: '' } }])
    setSelectedId(id)
  }

  function handleChange(fields: FieldWithId[]) {
    setDraft(fields)
    onChange(fields.map(({ id, ...field }) => field))
  }

  function updateSelected(field: Partial<FieldWithId>) {
    handleChange(draft.map(f => (f.id === selectedId ? { ...f, ...field } : f)))
  }

  return (
    <Flex direction="column" gap={2}>
      <Text textStyle="h5">Fields</Text>
      <Flex gap={8}>
        <Flex
          direction="column"
          gap={3}
          borderWidth="1px"
          borderColor="gray.300"
          borderRadius="md"
          width="300px"
          flexShrink={0}
          minHeight="275px"
          p={4}
          {...(!draft.length && {
            cursor: 'pointer',
            onClick: handleAdd,
            _hover: { bg: 'gray.100' },
          })}
        >
          {draft.length ? (
            <>
              <DragList
                items={draft}
                containerProps={{ gap: 2 }}
                onChange={handleChange}
                renderItem={({ label, property, required, id }) => (
                  <Flex
                    align="center"
                    flex={1}
                    gap={2}
                    onClick={() => id !== selectedId && setSelectedId(id)}
                  >
                    <DotsDragIcon cursor="grab" />

                    <Flex
                      flex={1}
                      gap={2}
                      align="center"
                      borderWidth="1px"
                      borderColor={
                        duplicateProperties.includes(property)
                          ? 'danger.500'
                          : id === selectedId
                            ? 'primary.500'
                            : !property
                              ? 'danger.500'
                              : 'gray.300'
                      }
                      backgroundColor={id === selectedId ? 'primary.200' : 'white'}
                      borderRadius="md"
                      overflow="hidden"
                      cursor="pointer"
                      pl={3}
                    >
                      <Text lineClamp={1}>{label.en || property || 'New Field'}</Text>
                      {required && (
                        <Tooltip
                          content="This field is required"
                          positioning={{ placement: 'right' }}
                        >
                          <ExclamationCircleIcon type="outline" size="sm" />
                        </Tooltip>
                      )}
                      <Spacer />
                      <IconButton
                        aria-label="Delete Field"
                        icon={<XMarkIcon />}
                        onClick={e => {
                          e.stopPropagation()
                          const updated = draft.filter(f => f.id !== id)
                          handleChange(updated)
                          setSelectedId(updated[0]?.id)
                        }}
                      />
                    </Flex>
                  </Flex>
                )}
              />

              <Button leftIcon={<PlusIcon />} variant="outline" onClick={handleAdd}>
                Add
              </Button>
            </>
          ) : (
            <Flex direction="column" align="center" flex={1} justify="center" gap={3}>
              <PlusIcon size="lg" />
              <Text textAlign="center" width="220px">
                Adding fields will enable registration for this template
              </Text>
            </Flex>
          )}
        </Flex>

        <Flex
          display={selectedId != null ? 'flex' : 'none'}
          flex={1}
          maxWidth="600px"
          direction="column"
          gap={4}
        >
          <Text mt={2} textStyle="bodyMedium">
            Field Properties
          </Text>
          <SelectedFormField field={selectedField} onChange={updateSelected} />
        </Flex>
      </Flex>
    </Flex>
  )
}

const defaultFields: FormField[] = [
  { property: 'email', label: { en: 'Email' }, required: true },
  { property: 'company', label: { en: 'Company' }, required: false },
  { property: 'firstName', label: { en: 'First Name' }, required: false },
  { property: 'lastName', label: { en: 'Last Name' }, required: false },
]

interface SelectedFormFieldProps {
  field?: FieldWithId
  onChange: (field: Partial<FieldWithId>) => void
}

function SelectedFormField({ field, onChange }: SelectedFormFieldProps) {
  const { workspace } = useWorkspace()

  const { property, label, required, min, max } = field || {}
  const workspaceProperties = workspace?.properties || []
  const propertyOptions = [
    ...primaryLeadProperties.map(p => ({ value: p, label: startCase(p) })),
    ...workspaceProperties.map(p => ({ value: p.title })),
  ]

  const type = getPropertyType(property, workspaceProperties)

  return (
    <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
      <Flex direction="column" gap={4}>
        <InputContainer required label="Property Name">
          <Menu
            options={propertyOptions}
            autoFocus
            variant="outline"
            value={property as PrimaryLeadProperty}
            onChange={v => onChange({ property: v })}
          />
        </InputContainer>
        <TextField
          value={label?.en || startCase(property)}
          disabled={!property}
          label="Field Label"
          onBlur={() => label?.en && onChange({ label: { en: label.en.trim() } })}
          onChange={e => onChange({ label: { en: e.target.value } })}
        />
      </Flex>
      <Flex direction="column" justify="flex-end" gap={4}>
        <CheckBox
          children="Required"
          h="42.5px" // match text box height
          disabled={!property}
          checked={required || false}
          onChange={() => onChange({ required: !required })}
        />
        <InputContainer
          label="Character Limits"
          visibility={type === 'text' || type === 'number' ? 'visible' : 'hidden'}
        >
          <Flex justifyContent="space-between" gap={4}>
            <TextField
              placeholder="Min"
              type="number"
              min={0}
              disabled={!property}
              value={min ?? ''}
              onChange={e => onChange({ min: e.target.value ? Number(e.target.value) : undefined })}
            />
            <TextField
              placeholder="Max"
              type="number"
              min={0}
              disabled={!property}
              value={max ?? ''}
              onChange={e => onChange({ max: e.target.value ? Number(e.target.value) : undefined })}
            />
          </Flex>
        </InputContainer>
      </Flex>
    </SimpleGrid>
  )
}

function getPropertyType(
  property?: string,
  properties?: WorkspaceProperty[]
): WorkspacePropertyType {
  if (!property) return 'text'

  const workspaceProperty = properties?.find(p => p.title === property)
  if (workspaceProperty) return workspaceProperty.type

  switch (property) {
    case 'email':
      return 'email'
    case 'phone':
      return 'phone'
    default:
      return 'text'
  }
}
